import React from 'react';

import { enqueueSnackbar } from 'notistack';
import { FormProvider, useForm } from 'react-hook-form';
import { Button } from '@infoods/lifebase-ui';

import classes from './FoodForm.module.scss';
import { openConfirmationModal } from '../../../../shared/features/confirmation';
import { goTo } from '../../../../shared/utils/angularUtils';
import { FoodType } from '../../../constants/foodType';
import { foodsService } from '../../../services/foodsService';
import { foodTemplateStorage, goToAddFoodWithTemplate } from '../../../utils';
import { FoodFormInitialData } from '../interfaces/FoodFormInitialData';
import { FoodFormValues } from '../interfaces/FoodFormValues';
import { saveFood } from '../utils/saveFood/saveFood';
import { FoodDetailsCard } from '../FoodDetailsCard/FoodDetailsCard';
import { MicroNutrientsCard } from '../MicroNutrientsCard/MicroNutrientsCard';
import { NutrientsCard } from '../NutrientsCard/NutrientsCard';
import { HiddenInput } from '../../../../shared/components/form-components';

type FoodFormProps = {
  foodType: FoodType;
  isEdit: boolean;
  /** read once on mount - the form is not reset when it changes */
  initialData: FoodFormInitialData;
  /** set in edit only - shows Delete */
  foodId?: number;
  /** the prefill template; removed from storage after a successful save */
  templateId?: string;
};

const FoodForm = ({
  foodType,
  isEdit,
  initialData,
  foodId,
  templateId,
}: FoodFormProps) => {
  const {
    food,
    defaultValues,
    nxMeasurementsByUnit,
    amountUnitOptions,
    tags,
    brands,
    microNutrientOptions,
  } = initialData;

  const isNXFood = !!food?.my_net_diary_bean_id;
  const formMethods = useForm<FoodFormValues>({ defaultValues });
  const {
    handleSubmit,
    setValue,
    getValues,
    register,
    formState: { isSubmitting },
  } = formMethods;

  const goToList = () => goTo(`/foods/${foodType}`);

  const handleSave = handleSubmit(async (formValues) => {
    await saveFood({
      foodType,
      formValues,
      nxMeasurementsByUnit,
      onFoodIdReceived: (id) => setValue('id', id),
    });

    if (templateId) {
      foodTemplateStorage.remove(templateId);
    }

    enqueueSnackbar(`${formValues.name} saved`, { variant: 'success' });
    goToList();
  });

  const handleDeleteConfirm = async (id: number, name: string) => {
    await foodsService.deleteFood(id);

    enqueueSnackbar(`${name} deleted`, { variant: 'success' });
    goToList();
  };

  const handleDeleteClick = (id: number) => {
    const name = getValues('name') || defaultValues.name;

    openConfirmationModal({
      title: `Are you sure you want to delete ${name}?`,
      confirmBtnConfig: { onClick: () => handleDeleteConfirm(id, name), text: 'Delete' },
    });
  };

  const handleEditNutrients = () => {
    if (!food) {
      console.error('Food not found in initial data');
      return;
    }

    openConfirmationModal({
      title: 'Want to Edit the Nutrients?',
      text: "You can't edit the nutrients directly. Go to Add Food to edit a copy of this food.",
      confirmBtnConfig: {
        onClick: () => goToAddFoodWithTemplate(food, foodType),
        text: 'Go to Add Food',
      },
    });
  };
  // we can't edit nutrients and micro nutrients for NX foods
  const isHiddenMicroNutrients = isNXFood && isEdit && food.microNutrients.length === 0;

  return (
    <FormProvider {...formMethods}>
      <form noValidate onSubmit={handleSave}>
        <HiddenInput register={register} name="id" />
        <FoodDetailsCard
          foodType={foodType}
          amountUnitOptions={amountUnitOptions}
          nxMeasurementsByUnit={nxMeasurementsByUnit}
          tags={tags}
          brands={brands}
        />
        <NutrientsCard
          mode={isNXFood && isEdit ? 'VIEW' : 'EDIT'}
          onEdit={handleEditNutrients}
        />
        {!isHiddenMicroNutrients && (
          <MicroNutrientsCard microNutrientOptions={microNutrientOptions} />
        )}
        <div className={classes.actions}>
          {foodId !== undefined && (
            <Button
              variant="outlined"
              size="large"
              onClick={() => handleDeleteClick(foodId)}
              data-test-id="delete-food-btn"
            >
              Delete
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={isSubmitting}
            data-test-id="save-food-btn"
          >
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export { FoodForm };
